import { ClientSideOnly } from '@/components/clientSideOnly';
import { cn } from '@/utils/shadcn';
import { DataHeader } from '@/components/dataHeader';
import { getAsQuery } from '@/components/filters/filterTools';
import { NoRecords } from '@/components/noDataMessages';
import { PaginationFooter } from '@/components/pagination';
import { CardListSkeleton } from '@/components/skeletonLoaders';
import { Card, CardHeader, CardTitle } from '@/components/ui/largeCard';
import { Skeleton } from '@/components/ui/skeleton';
import { FilterContext, FilterProvider } from '@/contexts/filter';
import { SearchContextProvider, useSearchContext } from '@/contexts/search';
import { useFilterParams } from '@/dataManagement/filterAdapter/useFilterParams';
import {
  EventFiltering,
  Predicate,
  ResourceSearchQuery,
  ResourceSearchQueryVariables,
  ResourceSortBy,
  ResourceSortOrder,
} from '@/gql/graphql';
import { useNumberParam, useParam } from '@/hooks/useParam';
import useUpdateEffect from '@/hooks/useUpdateEffect';
import React, { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArticleContainer } from '../key/components/articleContainer';
import { ArticleTextContainer } from '../key/components/articleTextContainer';
import { useFilters } from './filters';
import { HeaderActionButtons } from './headerActionButtons';
import { useResourceSearchMetadata } from './resourceSearchMetadata';
import { ResourceSearchResult } from './resourceSearchResult';
import { ResourceSearchTabs } from './resourceSearchTabs';
import { searchConfig } from './searchConfig';
import { orderedTabs, tabsConfig } from './tabsConfig';
import { FilterBarWithActions } from '@/components/filters/filterBarWithActions';
import {
  MobileFiltersTrigger,
  useIsMobileFilterSheetActive,
} from '@/components/filters/mobileFilters';
import PageMetaData from '@/components/PageMetaData';
import { Resource, useResourceSearch, RESOURCE_SEARCH_QUERY } from './useResourceSearch';
import {
  getDefaultResourceSort,
  getResourceSortVariables,
  ResourceSortValue,
  useResourceSort,
} from './useResourceSort';

export { RESOURCE_SEARCH_QUERY };
export type { Resource };

export function extractValidResourceSearchResults(
  searchResult: ResourceSearchQuery['resourceSearch'] | undefined
): Resource[] {
  return (
    (searchResult?.documents?.results?.filter(
      (result) => result != null && 'id' in result
    ) as Resource[]) || []
  );
}

const DEFAULT_TAB = 'all';

export function ResourceSearchPage(): React.ReactElement {
  const [tab] = useParam({
    hideDefault: true,
    key: 'contentType',
    parse: (value) =>
      typeof value === 'string' && orderedTabs.includes(value) ? value : DEFAULT_TAB,
  });

  const [filter, setFilter] = useFilterParams({
    filterConfig: searchConfig,
    paramsToRemove: ['offset', 'from'],
  });

  const searchMetadata = useResourceSearchMetadata(tab);

  const intl = useIntl();

  return (
    <>
      <PageMetaData
        path="/resource/search"
        title={intl.formatMessage({ id: 'resourceSearch.title' })}
        description={intl.formatMessage({ id: 'resourceSearch.resourceDescription' })}
      />

      <SearchContextProvider searchContext={searchMetadata}>
        <FilterProvider filter={filter} onChange={setFilter}>
          <ResourceSearchPageInner activeTab={tab} defaultTab={DEFAULT_TAB} />
        </FilterProvider>
      </SearchContextProvider>
    </>
  );
}

type Props = {
  activeTab: string;
  defaultTab: string;
};

function ResourceSearchPageInner({ activeTab, defaultTab }: Props): React.ReactElement {
  const searchContext = useSearchContext();
  const { filters } = useFilters({ searchConfig });
  const filterSheetActive = useIsMobileFilterSheetActive(filters);
  const [offset, setOffset] = useNumberParam({ key: 'offset', defaultValue: 0, hideDefault: true });
  const [sort, setSort] = useResourceSort();
  const filterContext = useContext(FilterContext);

  const { filter, filterHash } = filterContext || { filter: { must: {} } };

  const variables = useMemo<ResourceSearchQueryVariables>(() => {
    const query = getAsQuery({ filter, searchContext, searchConfig }) as {
      predicate: Predicate | undefined;
      q: string | undefined;
      eventFiltering: EventFiltering | undefined;
    };

    const sortingOptions: Pick<ResourceSearchQueryVariables, 'sortBy' | 'sortOrder'> =
      getResourceSortVariables(sort ?? getDefaultResourceSort(Boolean(query.q)));

    if ('eventFiltering' in query && activeTab === 'event') {
      switch (query.eventFiltering) {
        case EventFiltering.Past:
          sortingOptions.sortBy = ResourceSortBy.Start;
          sortingOptions.sortOrder = ResourceSortOrder.Desc;
          break;
        case EventFiltering.Upcoming:
          sortingOptions.sortBy = ResourceSortBy.Start;
          sortingOptions.sortOrder = ResourceSortOrder.Asc;
      }
    }

    return { ...query, ...sortingOptions };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterHash, searchContext, activeTab, sort]);

  const { loading, resources, total, size } = useResourceSearch({ variables, offset });

  const hasQuery = Boolean(variables.q);
  const effectiveSort = sort ?? getDefaultResourceSort(hasQuery);

  // Scroll to top when changing filter or offset
  useUpdateEffect(() => {
    window.scrollTo(0, 0);
  }, [filterHash, offset]);

  return (
    <>
      <DataHeader
        className="g-bg-white"
        title={<FormattedMessage id="catalogues.resources" defaultMessage="Resources" />}
        hasBorder
        // aboutContent={<AboutContent />}
        // apiContent={<ApiContent />}
        mobileFiltersTrigger={<MobileFiltersTrigger filters={filters} />}
      >
        <ResourceSearchTabs activeTab={activeTab} defaultTab={defaultTab} />
      </DataHeader>

      <Card className={cn(filterSheetActive && 'g-hidden sm:g-block')}>
        <FilterBarWithActions filters={filters} hideMobileFilters />
      </Card>

      <ArticleContainer className="g-bg-slate-100 g-flex">
        <ArticleTextContainer className="g-flex-auto g-w-full">
          <ResourceSearchResults
            loading={loading}
            resources={resources}
            activeTab={activeTab}
            total={total}
            size={size}
            offset={offset}
            setOffset={setOffset}
            sort={effectiveSort}
            onSortChange={setSort}
            hasQuery={hasQuery}
          />
        </ArticleTextContainer>
      </ArticleContainer>
    </>
  );
}

type ResourceSearchResultsProps = {
  loading: boolean;
  resources: Resource[];
  activeTab: string;
  total?: number;
  size?: number;
  offset: number;
  setOffset: (offset: number) => void;
  disableHeaderActionButtons?: boolean;
  sort?: ResourceSortValue;
  onSortChange?: (sort: ResourceSortValue) => void;
  hasQuery?: boolean;
  noResultsMessageId?: string;
};

export function ResourceSearchResults({
  loading,
  resources,
  activeTab,
  total,
  size,
  offset,
  setOffset,
  disableHeaderActionButtons,
  sort,
  onSortChange,
  hasQuery,
  noResultsMessageId,
}: ResourceSearchResultsProps) {
  if (loading || total === undefined) {
    return (
      <>
        <CardHeader>
          <Skeleton className="g-max-w-64">
            <CardTitle>
              <FormattedMessage id="phrases.loading" />
            </CardTitle>
          </Skeleton>
        </CardHeader>
        <CardListSkeleton />
      </>
    );
  }

  if (resources.length === 0) {
    return (
      <div className="g-min-h-52 g-flex g-items-center g-justify-center">
        <NoRecords messageId={noResultsMessageId} />
      </div>
    );
  }

  if (resources.length > 0) {
    return (
      <>
        <CardHeader className="g-flex-col md:g-flex-row g-items-start md:g-items-center g-justify-between">
          <CardTitle>
            <FormattedMessage id={tabsConfig[activeTab].countKey} values={{ total: total ?? 0 }} />
          </CardTitle>

          {!disableHeaderActionButtons && (
            <HeaderActionButtons
              activeTab={activeTab}
              sort={sort}
              onSortChange={onSortChange}
              hasQuery={hasQuery}
            />
          )}
        </CardHeader>
        <ul>
          {resources.map((resource) => (
            <li key={resource.id}>
              <ResourceSearchResult resource={resource} className="g-bg-white g-mb-4" />
            </li>
          ))}
        </ul>
        <ClientSideOnly>
          {total != null && size != null && total > size && (
            <PaginationFooter
              offset={offset}
              count={total}
              limit={size}
              onChange={(x) => {
                setOffset(x);
              }}
            />
          )}
        </ClientSideOnly>
      </>
    );
  }

  return null;
}
